import { useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { find } from "lodash";
import {
  getCategoryTrafficTypes,
  getOfferTrafficTypes,
  getTrafficTypes,
  getTrafficTypesByTypeAndCategory
} from 'cityads-v3/actions/trafficTypes';
import { BANNED_TRAFFIC_TYPE_IDS, TRAFFIC_TYPE_VALUE_ALLOWED } from "cityads-v3/reducers/trafficTypes";
import { RootState } from "cityads-v3/types/reducer";
import { TrafficTypeItemList, TrafficTypeItemWithValue } from "cityads-v3/types/trafficTypes";
import { Offer } from "cityads-v3/types/offers";
import { TUseEntities, useEntities } from "./entities";

/**
 * Получить список типов трафика с учетом ограничений в зависимости от языка
 */
export function useTrafficTypesEntitiesWithRestrictions(aliasName?: string, autoLoad?: boolean): TUseEntities<TrafficTypeItemList> {
  const lang = useSelector((state: RootState) => state.language.language);
  const trafficTypesEntities = useEntities<TrafficTypeItemList>('trafficTypes', aliasName, autoLoad);

  return [
    trafficTypesEntities[0]?.filter(trafficType => (
      lang !== 'ru' || !BANNED_TRAFFIC_TYPE_IDS.includes(trafficType.id)
    )),
    trafficTypesEntities[1],
    trafficTypesEntities[2],
    trafficTypesEntities[3],
    trafficTypesEntities[4],
  ]
}

/**
 * Получить список стандартных типов трафика по офферу с учетом категории оффера
 * и со значениями "разрешен/запрещен"
 * @param offerId
 */
export function useOfferTrafficTypes(offerId: Offer['id']) {
  const offer = useSelector((state: RootState) => state.offers.offersById[offerId])
  const categoryId = offer?.business_category
  const actionTypeDirectionId = offer?.action_type_direction_id

  usePreloadTrafficTypes()
  usePreloadOfferTrafficTypes(offerId)
  usePreloadStandardTrafficTypeIds(actionTypeDirectionId, categoryId)
  usePreloadCategoryTrafficTypes(actionTypeDirectionId, categoryId)

  const trafficTypesById = useSelector((state: RootState) => state.trafficTypes.trafficTypesById)
  const offerTrafficTypesByTTId = useSelector((state: RootState) => state.trafficTypes.offerTrafficTypesByOfferIdAndTTId[offerId])
  const categoryTrafficTypes = useSelector((state: RootState) => state.trafficTypes.categoryTrafficTypes)
  const offerTrafficTypesLoading = useSelector((state: RootState) => state.trafficTypes.offerTrafficTypesLoadingByOfferId[offerId]);
  const standardTrafficTypeIdsLoading = useSelector((state: RootState) => state.trafficTypes.trafficTypesLoadingByActionTypeDirectionIdAndCategoryId[actionTypeDirectionId]?.[categoryId])

  const offerTrafficTypeIds = useSelector((state: RootState) => (
    state.trafficTypes.trafficTypeIdsByActionTypeDirectionIdAndCategoryId[actionTypeDirectionId]?.[categoryId]
  ))

  const standardOfferTrafficTypes = useMemo<TrafficTypeItemWithValue[]>(() => (
    (standardTrafficTypeIdsLoading || offerTrafficTypesLoading)
      ? []
      : offerTrafficTypeIds?.map(ttId => +ttId).map(ttId => {
        const trafficType = trafficTypesById?.[ttId];
        const offerTrafficType = offerTrafficTypesByTTId?.[ttId] ?? {
          value: find(categoryTrafficTypes?.[categoryId], { traffic_type_id: ttId })?.value ?? TRAFFIC_TYPE_VALUE_ALLOWED,
          note: ''
        };

        return {
          ...trafficType,
          value: offerTrafficType.value,
          note: offerTrafficType.note,
        }
      }).sort((item1, item2) => item1.position - item2.position) ?? []
  ), [
    standardTrafficTypeIdsLoading,
    offerTrafficTypesLoading,
    offerTrafficTypeIds,
    trafficTypesById,
    offerTrafficTypesByTTId,
    categoryTrafficTypes,
    categoryId,
  ])

  const nonStandardOfferTrafficTypes = useMemo<TrafficTypeItemWithValue[]>(() => (
    (standardTrafficTypeIdsLoading || offerTrafficTypesLoading)
      ? []
      : Object.keys(offerTrafficTypesByTTId ?? {}).filter(ttId => offerTrafficTypeIds?.map(id => +id).indexOf(+ttId) < 0).map(ttId => {
        const trafficType = trafficTypesById?.[ttId];
        const offerTrafficType = offerTrafficTypesByTTId[ttId];

        return {
          ...trafficType,
          value: offerTrafficType.value,
          note: offerTrafficType.note,
        }
      }).sort((item1, item2) => item1.position - item2.position)
  ), [
    standardTrafficTypeIdsLoading,
    offerTrafficTypesLoading,
    offerTrafficTypeIds,
    trafficTypesById,
    offerTrafficTypesByTTId,
  ])

  return {
    standardOfferTrafficTypes,
    nonStandardOfferTrafficTypes,
  };
}

export function usePreloadTrafficTypes() {
  const dispatch = useDispatch()
  const allTrafficTypesLoading = useSelector((state: RootState) => state.trafficTypes.isLoading)
  const allTrafficTypesLoaded = useSelector((state: RootState) => state.trafficTypes.isLoaded)

  useEffect(() => {
    if (!allTrafficTypesLoading && !allTrafficTypesLoaded) {
      dispatch(getTrafficTypes())
    }
  }, [dispatch, allTrafficTypesLoading, allTrafficTypesLoaded])
}

export function usePreloadStandardTrafficTypeIds(actionTypeDirectionId: Offer['action_type_direction_id'], categoryId: Offer['business_category']) {
  const dispatch = useDispatch()
  const standardTrafficTypeIdsLoading = useSelector((state: RootState) => state.trafficTypes.trafficTypesLoadingByActionTypeDirectionIdAndCategoryId[actionTypeDirectionId]?.[categoryId])
  const standardTrafficTypeIdsLoaded = useSelector((state: RootState) => state.trafficTypes.trafficTypesLoadedByActionTypeDirectionIdAndCategoryId[actionTypeDirectionId]?.[categoryId])

  useEffect(() => {
    if (
      !standardTrafficTypeIdsLoading && !standardTrafficTypeIdsLoaded
      && actionTypeDirectionId && categoryId
    ) {
      dispatch(getTrafficTypesByTypeAndCategory(actionTypeDirectionId, categoryId))
    }
  }, [
    dispatch,
    standardTrafficTypeIdsLoading,
    standardTrafficTypeIdsLoaded,
    actionTypeDirectionId,
    categoryId,
  ])

}

export function usePreloadOfferTrafficTypes(offerId: Offer['id']) {
  const dispatch = useDispatch()
  const offerTrafficTypesLoading = useSelector((state: RootState) => state.trafficTypes.offerTrafficTypesLoadingByOfferId[offerId]);
  const offerTrafficTypesLoaded = useSelector((state: RootState) => state.trafficTypes.offerTrafficTypesLoadedByOfferId[offerId]);
  useEffect(() => {
    if (!offerTrafficTypesLoading && !offerTrafficTypesLoaded && offerId) {
      dispatch(getOfferTrafficTypes(offerId))
    }
  }, [dispatch, offerTrafficTypesLoading, offerTrafficTypesLoaded, getOfferTrafficTypes, offerId])
}

export function usePreloadCategoryTrafficTypes(actionTypeDirectionId: Offer['action_type_direction_id'], categoryId: Offer['business_category']) {
  const dispatch = useDispatch();
  const categoryTrafficTypes = useSelector((state: RootState) => state.trafficTypes.categoryTrafficTypes)

  useEffect(() => {
    if (
      !categoryTrafficTypes[categoryId]
      && actionTypeDirectionId && categoryId
    ) {
      dispatch(getCategoryTrafficTypes(actionTypeDirectionId, categoryId))
    }
  }, [
    dispatch,
    actionTypeDirectionId,
    categoryTrafficTypes,
    categoryId
  ])
}
